import { Formik } from "formik";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useJsApiLoader } from "@react-google-maps/api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as apicall from "../../../components/Apicall";
import InvoiceCancelDialog from "../../../components/Invoice/InvoiceCancelDialog";
import InvoiceDateDialog from "../../../components/Invoice/InvoiceDateDialog";
import Stepper from "../../../components/Stepper/Stepper";
import { google_places_key } from "../../../constants/Constants";
import { getPersonaForRole, PERSONA } from "../../../constants/roles";
import { getInvoiceAssets } from "../../../services/customerAssetService";
import * as estimationService from "../../../services/estimationService";
import * as inventoryService from "../../../services/inventoryService";
import * as invoiceService from "../../../services/invoiceService";
import { describeConflict } from "../../../services/resourceService";
import { getBookingWorkflow } from "../../../services/workflowService";
import { fetchBookingStatusesForIndustry } from "../../../utils/bookingStatus";
import { getBookingLabel } from "../../../utils/industryLabels";
import { parseBookingRef } from "../../../utils/bookingRef";
import { buildInvoiceHtml, splitIntoOfferings } from "../../../utils/invoiceHtml";
import { useActiveResources } from "../ResourceField";
import { useBookingWorkflow } from "../workflow/WorkflowPanel";
import { assignmentsSignature, isServiceStepOptional } from "../workflow/workflowAssignments";
import BookingModals from "./BookingModals";
import {
  amountsSignature,
  buildSubmitValues,
  customItemsSignature,
  lineDiscountsSignature,
  hhmm,
  isBookingUnchanged,
  normalizeSpecifics,
  providerIdsKey,
  specificsChanged
} from "./bookingPayload";
import { getBookingCoupons } from "../../../services/brandingService";
import { computePriceSection, couponAppliesToBooking, DISCOUNT_FLAT, lineKey, pricingProblems } from "./bookingPricing";
import { buildBookingSchema } from "./bookingSchema";
import { buildDaySlots, cleanCrewSlots, convertWithoutTimseStamp, getStatusColors, resolveSlotClick } from "./bookingSlots";
import {
  cleanBackendValue,
  crewNoun,
  dateToYmd,
  FIELD_LABELS,
  stepOfField,
  STEP_FIELDS
} from "./bookingStepperUtils";
import BookingStepperForm from "./BookingStepperForm";
import useBookingCheckout from "./useBookingCheckout";
import { slotMessage } from "./TimeSlotPicker";
import useVendorTimeSlots, { ymdOf } from "./useVendorTimeSlots";
import { pickNearestSlot } from "./nearestSlot";
import "./bookingStepper.css";

// Invoice record shown on the booking page (from get_ezp_booking_by_id), or null if not invoiced.
const toInvoiceInfo = (b) =>
  b?.invoice_ref_id ? { ref: b.invoice_ref_id, number: b.invoice_number, url: b.invoice_url } : null;

// get_ezp_booking_by_id.provider_people = "12:Ravi K||14:Imran S" -> Map(12 => "Ravi K")
const parseProviderPeople = (rows) => {
  const map = new Map();
  (rows || []).forEach((r) =>
    String(r?.provider_people || "")
      .split("||")
      .filter(Boolean)
      .forEach((pair) => {
        const i = pair.indexOf(":");
        const id = Number(pair.slice(0, i));
        if (id > 0) map.set(id, pair.slice(i + 1).trim());
      })
  );
  return map;
};

// A provider saved on the booking who has no slot on that day's crew calendar (assigned later, or no
// shift that day) still belongs to the booking: kept as a restored entry so saving never drops them.
// `restored` entries are not calendar picks - they neither set the time nor get cleared by a new time.
const restoredProviderEntry = (id, name, startIso) => ({
  title: name || `Provider #${id}`,
  start: startIso,
  end: startIso,
  status: 2,
  fromCalendar: false,
  restored: true,
  extendedProps: { provider_id: id, search: name || `Provider #${id}` }
});

/**
 * New Booking / Job Card (and the edit screen of a saved one) for every non-clinic industry, as a
 * four-step flow: Customer -> Service -> Crew & Schedule -> Review & Pay, with a live summary.
 *
 * This is the page Bookings.jsx used to be, re-laid-out. The state and handlers below are that
 * page's, moved over unchanged (the form has a lot of hard-won race handling - see the comments
 * - so it was re-arranged, not rewritten); what is new is the step navigation, the discount /
 * quantity / tip pricing (bookingPricing.js), and taking payment in the last step
 * (useBookingCheckout.js) instead of on a separate Checkout page. The clinic's slider
 * (NewAppointment.jsx) is a different component and is untouched.
 */
function BookingStepper() {
  const routeLocation = useLocation();
  let service = JSON.parse(localStorage.getItem("services"));
  let vendor_id = JSON.parse(localStorage.getItem("userId"));
  const vendorProfile = JSON.parse(localStorage.getItem("vendorProfile")) || {};

  // const _ud = (() => {
  //   try {
  //     const d = localStorage.getItem("userDetails");
  //     return d && d !== "undefined" ? JSON.parse(d) : null;
  //   } catch {
  //     return null;
  //   }
  // })();

  const taxPercentage = parseFloat(vendorProfile?.tax || 0);
  // const [searchParams] = useSearchParams();
  // let group_id = searchParams.get("group_id");
  // The route carries the group id or the display booking id; an unreadable one
  // is stopped earlier, in BookingStepper/index.jsx.
  let { group_id: groupIdParam } = useParams();
  let group_id = parseBookingRef(groupIdParam).groupId || undefined;

  // Convert-to-booking entry point: "Convert to Booking" on an accepted
  // estimation navigates here as /vendor_bookings?from_estimation=<id> (a
  // query param, not a route param, since there's no group_id yet - this
  // is always a brand-new booking, never an edit). Deliberately does NOT
  // set group_id, so the submit handler below still takes the "new
  // booking → Checkout" path instead of the in-place update path.
  const fromEstimationId = new URLSearchParams(routeLocation.search).get(
    "from_estimation"
  );
  const [services, setServices] = useState(service);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);

  const [initialValues, setInitialValues] = useState({
    partner: 1,
    org_id: "",
    userName: "",
    phoneNumber: "",
    email: "",
    house_no: "",
    area: "",
    location: "",
    location_id: "",
    state: "",
    landmark: "",
    country: "",
    lat: "",
    lng: "",
    // time: "",
    status: 2, // In Progress — default service status for a new booking
    payment: 0
  });

  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(service?.[0] || {});
  const [initialServiceData, setInitialServiceData] = useState([]);
  const [serviceData, setServiceData] = useState([]);
  // Latest serviceData for fetchServiceData's async continuation, which
  // otherwise only sees the render it was called from.
  const serviceDataRef = useRef(serviceData);
  serviceDataRef.current = serviceData;
  const [serviceTypes, setServiceTypes] = useState([]);
  const [activeType, setActiveType] = useState("null");
  const [groupedData, setGroupedData] = useState({});
  const [dateSelect, setDateSelect] = useState("");
  const [timeSelect, setTimeSelect] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  // Discount: a flat amount or a % of the order, optionally also per service line; plus a
  // quantity per service line and a tip (new bookings). All of it is folded into the Discount /
  // Total rows of priceSection by getPrice(), so the API payload keeps its shape.
  const [discountSpec, setDiscountSpec] = useState({ type: DISCOUNT_FLAT, value: 0 });
  // Each service line's own discount { [service_offering_id]: { type, value } }, saved per
  // offering (line_discount). Separate from discountSpec, the overall order discount.
  const [lineDiscounts, setLineDiscounts] = useState({});
  const [lineQty, setLineQty] = useState({});
  const [tipPercent, setTipPercent] = useState(0);
  // Vendor coupons (Branding & Offers > Coupons) usable today, and the one picked on the Review step.
  const [bookingCoupons, setBookingCoupons] = useState([]);
  const [couponId, setCouponId] = useState(null);
  const loadBookingCoupons = () => {
    getBookingCoupons()
      .then((res) => setBookingCoupons(res?.data || []))
      .catch(() => setBookingCoupons([]));
  };
  // The flat discount that results (what the API stores, and the edit screen's "unchanged" check).
  const [discount, setDiscount] = useState(0);
  const [priceSection, setPriceSection] = useState([]);
  // Edit-booking only: live parts subtotal lifted up from PartsUsedSection,
  // folded into getPrice()'s total. originalPartsSubtotalRef baselines it at
  // whatever value this session's first onPartsChanged call reports (i.e.
  // the booking's pre-existing parts, if any), so the balance-increase
  // banner below only reacts to parts added/removed during this edit
  // session, not parts the booking already had.
  const [editPartsSubtotal, setEditPartsSubtotal] = useState(0);
  const originalPartsSubtotalRef = useRef(null);
  const [providerSelected, setProviderSelected] = useState([]);
  // Optional bookable resource (bay, van, room, court, a driver ...). Kept
  // like providerSelected - outside Formik - and only ever sent to the API
  // when the field is on screen. A vendor with no resources sees no field and
  // sends no `resource_ids` key, so nothing about their booking changes.
  const { resources: activeResources } = useActiveResources();
  const [resourceIds, setResourceIds] = useState("");
  // Choosing a resource narrows the crew calendar to the people mapped to it; this shows everyone again.
  const [showAllCrew, setShowAllCrew] = useState(false);
  useEffect(() => {
    setShowAllCrew(false);
  }, [resourceIds]);
  const [resourceCurrentName, setResourceCurrentName] = useState("");
  // Booking workflow (Setup > Workflows): who does each step. Only present for a
  // service that has a workflow; for any other the panel is absent and no
  // `workflow` key is sent. Kept outside Formik like providerSelected.
  const {
    definition: workflowDef,
    initialAssignments: workflowInitial,
    stateKey: workflowStateKey,
    existing: workflowExisting
  } = useBookingWorkflow({ priceSection, groupId: group_id });
  // A saved booking that already has a workflow shows it in motion (Start / Done ... are
  // saved straight away); a booking without one gets the assign-on-save panel. Either way
  // WHO is on each step is part of this form and is saved by Update.
  const [runWorkflow, setRunWorkflow] = useState(null);
  useEffect(() => {
    setRunWorkflow(workflowExisting);
  }, [workflowExisting]);
  const formWorkflow = workflowDef || null;
  // The booking's own workflow is shown while the service still resolves to it; if the
  // service was changed to one with a different workflow (and nothing has started), the
  // panel shows the new one - what the server will switch to on save.
  const showRunWorkflow =
    Boolean(group_id) &&
    !!runWorkflow &&
    (!workflowDef || Number(workflowDef.template_id) === Number(runWorkflow.template_id));
  const [workflowAssign, setWorkflowAssign] = useState({});
  // What a "back from Checkout" navigation carried, so it survives the reload of the definition.
  const workflowRestoreRef = useRef(null);
  const workflowInitialSigRef = useRef("");
  useEffect(() => {
    const start = workflowRestoreRef.current || workflowInitial || {};
    workflowRestoreRef.current = null;
    setWorkflowAssign(start);
    workflowInitialSigRef.current = assignmentsSignature(workflowDef, workflowInitial || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowStateKey, workflowDef?.template_id]);
  const [locationError, setLocationError] = useState("");
  // Fixes the old page's `useParams().id` (the route param is group_id), which kept this false.
  const isEditing = Boolean(group_id);
  // The workflow's service step is Optional: the vendor books a time now and assigns the service
  // provider later. A saved booking follows ITS OWN steps (what it was made with), a new one the
  // workflow of the service being booked. No workflow = the provider stays required, as always.
  const providerOptional = isServiceStepOptional(showRunWorkflow ? runWorkflow?.steps : formWorkflow?.steps);
  let navigate = useNavigate();

  // Crew persona: view + edit existing bookings only, no new booking
  // creation. This route serves both create (no group_id) and edit
  // (:group_id) from one component, so this single guard - rather than
  // hiding every "+ New Booking" button across the app - is what actually
  // makes the restriction correct even if an entry point is missed.
  useEffect(() => {
    if (getPersonaForRole(apicall.getRole()) === PERSONA.CREW && !group_id) {
      navigate("/access-denied");
    }
  }, [group_id]);
  const [timeRange, setTimeRange] = useState({ startTime: "", endTime: "" });

  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [initialAppointments, setInitialAppointments] = useState([]);
  const [partner_id, setPartner_id] = useState(1);
  const [partners, setPartners] = useState([]);
  // Coupons the Review step may offer for this booking: right profile, and (if the coupon is limited
  // to some services) at least one of those services added. The picked one drops out if that changes.
  const couponOptions = bookingCoupons.filter((c) =>
    couponAppliesToBooking(c, partner_id, serviceData.filter((d) => d?.added).map((d) => d.service_id))
  );
  const activeCoupon = couponOptions.find((c) => c.id === couponId) || null;
  useEffect(() => {
    if (!isEditing) loadBookingCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [user_id, setUser_id] = useState(0);
  const [userFlexiSubs, setUserFlexiSubs] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [existingSubscriptionId, setExistingSubscriptionId] = useState(null);
  const [bookingStatus, setBookingStatus] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [firstLocation, setFirstLocation] = useState(null);
  const [existingData, setExistingData] = useState([]);
  // Invoice record of the booking being edited ({ ref, number, url }) or null. Comes with
  // get_ezp_booking_by_id; refreshed after generating / cancelling without reloading the form.
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [showCancelInvoice, setShowCancelInvoice] = useState(false);
  // Generate Invoice popup (asks for the invoice date).
  const [showInvoiceDate, setShowInvoiceDate] = useState(false);
  useEffect(() => {
    setInvoiceInfo(toInvoiceInfo(existingData?.[0]));
  }, [existingData]);
  const [open, setOpen] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [maxTime, setMaxTime] = useState(30);
  const [showOrderConf, setShowOrderConf] = useState(false);
  const [modelData, setModelData] = useState({});
  const [display_booking_id, setDisplay_booking_id] = useState("");
  const [editable, setEditable] = useState(true);
  const [currency, setCurrency] = useState(
    vendorProfile?.currency || localStorage.getItem("currency") || "AED"
  );
  const [calenderMessage, setCalenderMessage] = useState("");
  const [paidAmount, setPaidAmount] = useState(0);
  const latestRequestRef = useRef(0);
  const [industry, setIndustry] = useState([]);

  const isSportsService = industry?.industry === 7;
  const isCarService = industry?.industry === 8;
  const isSalonService = industry?.industry === 9;

  // Lifted from ServiceSpecificsSection (the real, industry-configurable
  // replacement for the old dead Car Details demo fields this used to be).
  // Persisted via a separate ezp_booking_service_details upsert, not part
  // of the main booking payload - see handleSubmit (edit) / Checkout.jsx (new).
  const [serviceSpecifics, setServiceSpecifics] = useState({ customer_asset_id: null, customer_complaint: "", action_taken: "" });

  // Guards fetchServiceData against the edit-mode race: selectedService starts
  // out seeded from localStorage (stale, from a previous session/booking), so
  // the [selectedService] effect fires fetchServiceData once immediately on
  // mount with the wrong service/partner/existingData, before getDataByID has
  // loaded the real booking and corrected selectedService/partner_id. Both
  // calls' responses land unordered — without this guard, whichever resolves
  // last wins and can silently overwrite the correct Service Category/Sub
  // Category/Service Type selection with the stale call's data.
  const latestServiceFetchRef = useRef(0);
  // What fetchServiceData last ran against — see the carry-over note there.
  const lastServiceFetchKeyRef = useRef({ existingData: null, partner: null });
  const pendingSubRef = useRef(null);
  const customerSelectedRef = useRef(false);
  const latestUserLookupRef = useRef(0);
  const checkoutReturnServiceDataRef = useRef(null);
  const originalSnapshotRef = useRef(null);
  // Parts Used (Feature 4) saves itself instantly via its own API calls, so
  // it never shows up in the "unchanged" diff below - without this flag, a
  // vendor who only added/removed parts and then hits Update sees the
  // confusing "No changes detected" warning even though they did change
  // something (it just didn't need this button to persist).
  const partsChangedRef = useRef(false);
  // Optional service step: the time list pre-selects the nearest slot. autoTimeRef is that pick (so it
  // can be replaced when the day or job length changes); timeTouchedRef is set once the vendor
  // chooses a time themselves, after which it is never overwritten.
  const autoTimeRef = useRef("");
  const timeTouchedRef = useRef(false);
  // Service Specifics lives in its own state/table too, so it needs its own
  // baseline for the "unchanged" diff - otherwise editing only the asset,
  // complaint or action-taken note reads as "No changes detected" and the
  // save below it never runs. Null until ServiceSpecificsSection reports
  // what's saved (treated as empty, see getSpecificsBaseline).
  const originalServiceSpecificsRef = useRef(null);
  const handleServiceSpecificsLoaded = useCallback((saved) => {
    originalServiceSpecificsRef.current = normalizeSpecifics(saved);
  }, []);
  const [vendorBlockedDates, setVendorBlockedDates] = useState([]);
  const resetBookingForm = () => {
    setInitialValues({
      partner: partner_id || 1,
      org_id: "",
      userName: "",
      phoneNumber: "",
      email: "",
      house_no: "",
      area: "",
      location: "",
      location_id: "",
      state: "",
      landmark: "",
      country: vendorProfile?.currency === "INR" ? 2 : 1,
      lat: "",
      lng: "",
      status: 2, // In Progress — default service status for a new booking
      payment: ""
    });

    let today = new Date();
    let d1 = today.toLocaleDateString("en-GB").split(/[/\-.]/);
    let d2 = `${d1[2]}-${d1[1]}-${d1[0]}`;
    setDateSelect(d2);
    setInitialServiceData([]);
    setServiceTypes([]);
    setGroupedData({});
    setActiveType("null");

    // setProviderSelected([]);
    setFilteredAppointments([]);
    setInitialAppointments([]);

    setTimeSelect("");
    autoTimeRef.current = "";
    timeTouchedRef.current = false;
    setAdditionalNotes("");
    setDiscount(0);
    setDiscountSpec({ type: DISCOUNT_FLAT, value: 0 });
    setLineDiscounts({});
    setLineQty({});
    setTipPercent(0);
    setPriceSection([]);

    setExistingData([]);
    setDisplay_booking_id("");
    setEditable(true);
    setUser_id(0);
    setResourceIds("");
    setResourceCurrentName("");
    customerSelectedRef.current = false;
  };
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await apicall.get("ezpMyProfile");
        const profile = response?.data?.[0];
        if (profile) {
          setCurrency(profile.currency || "AED");
          // industry (drives isCarService/isSportsService below, which
          // decide whether address fields are required) must come from the
          // fresh ezpMyProfile fetch, not localStorage — this was fetched
          // here already for currency, so reuse it instead of a second,
          // never-actually-called fetchVendorProfile request.
          setIndustry(profile);
          if (profile.id) fetchDoctorHolidays(profile.id);
        }
      } catch (e) {
        console.error("Failed to fetch profile for currency:", e);
      }
    };
    fetchProfile();
  }, []);
  useEffect(() => {
    if (!group_id) {
      // resetBookingForm();
      getAllServices();
    }
  }, [group_id]);
  const validationSchema = useMemo(
    () =>
      buildBookingSchema({ isSportsService, isCarService, isSalonService, partners, currency, countries }),
    [isSportsService, isCarService, isSalonService, partners, currency, countries]
  );

  useEffect(() => {
    getAllServices();
  }, [partner_id]);

  // Restore form when user navigates back from Checkout via "Add Item"
  useEffect(() => {
    const state = routeLocation.state;
    if (!state?.checkoutReturn || !state?.bookingValues) return;
    const bv = state.bookingValues;
    setInitialValues({
      partner: bv.partner || 1,
      org_id: bv.org_id || "",
      userName: bv.userName || "",
      phoneNumber: bv.phoneNumber || "",
      email: bv.email || "",
      house_no: bv.house_no || "",
      area: bv.area || "",
      location: bv.location || "",
      location_id: bv.location_id || "",
      state: bv.state || "",
      landmark: bv.landmark || "",
      country: bv.country || 1,
      lat: bv.lat || "",
      lng: bv.lng || "",
      status: bv.status || "",
      payment: bv.payment || ""
    });
    if (bv.date) setDateSelect(bv.date);
    if (bv.time) {
      setTimeSelect(bv.time);
      timeTouchedRef.current = true; // the time chosen before the payment redirect wins
    }
    if (bv.priceSection) setPriceSection(bv.priceSection);
    if (bv.additional_notes !== undefined)
      setAdditionalNotes(bv.additional_notes);
    if (bv.paid_amount !== undefined) setPaidAmount(bv.paid_amount);
    if (state.partner_id) setPartner_id(state.partner_id);
    // Restore service/type/offering selection — fetchServiceData will use this ref
    if (bv.serviceData?.length > 0) {
      checkoutReturnServiceDataRef.current = bv.serviceData;
      const addedItem = bv.serviceData.find((s) => s.added);
      if (addedItem?.service_type_name)
        setActiveType(addedItem.service_type_name);
    }
    // Restore crew/provider selection so form validation passes
    // Restore the optional resource too, so what was picked before "Add Item" survives.
    if (bv.resource_ids !== undefined) {
      setResourceIds(String(bv.resource_ids || ""));
    }
    if (bv.workflow?.steps?.length) {
      workflowRestoreRef.current = Object.fromEntries(
        bv.workflow.steps
          .filter((x) => x.user_id)
          .map((x) => [x.step_key, Number(x.user_id)])
      );
    }
    if (bv.providerSelectedData?.length > 0) {
      setProviderSelected(
        bv.providerSelectedData.map((s) => ({
          extendedProps: { provider_id: s.provider_id },
          start: s.start,
          end: s.end,
          title: s.title
        }))
      );
    }
    customerSelectedRef.current = true;
    // Clear navigation state so refreshing doesn't re-apply
    window.history.replaceState({}, "");
  }, [routeLocation.state]);

  // Show order confirmation modal when navigated back from Checkout after a new booking
  useEffect(() => {
    const state = routeLocation.state;
    if (state?.showOrderConf && state?.confData) {
      setModelData(state.confData);
      setShowOrderConf(true);
      window.history.replaceState({}, "");
    }
  }, [routeLocation.state]);

  useEffect(() => {
    fetchServiceData(selectedService);
  }, [selectedService]);

  useEffect(() => {
    // Skip only when restoring from Checkout's Back button - this effect
    // only ever fires once, on mount, and that's exactly when the
    // checkoutReturn restore effect above just set the real saved date.
    if (!routeLocation.state?.checkoutReturn) {
      let today = new Date();
      let d1 = today.toLocaleDateString("en-GB").split(/[/\-.]/);
      let d2 = `${d1[2]}-${d1[1]}-${d1[0]}`;
      setDateSelect(d2);
    }
    // getAllServices();
    fetchPartners();
  }, [group_id]);
  const autocompleteRef = useRef(null);

  const onLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: google_places_key,
    libraries: ["places"]
  });
  const onPlaceChanged = (setFieldValue) => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place || !place.address_components) return;

    let street = place.formatted_address || "";
    let area = "";
    let state = "";
    let country = "";
    let lat = place.geometry?.location?.lat();
    let lng = place.geometry?.location?.lng();

    place.address_components.forEach((comp) => {
      const types = comp.types;

      if (types.includes("sublocality") || types.includes("locality")) {
        area = comp.long_name;
      }

      if (types.includes("administrative_area_level_1")) {
        state = comp.long_name;
      }

      if (types.includes("country")) {
        country = comp.short_name?.toLowerCase(); //  "in" / "ae"
      }
    });

    //  expected country based on currency
    const expectedCountry = currency === "INR" ? "in" : "ae";

    // console.log("Selected:", country, "Expected:", expectedCountry);

    // VALIDATION
    if (!country || country !== expectedCountry) {
      setLocationError(
        currency === "INR"
          ? "Please select a location within India"
          : "Please select a location within UAE"
      );

      setFieldValue("location", "");
      setFieldValue("area", "");
      setFieldValue("state", "");
      setFieldValue("lat", "");
      setFieldValue("lng", "");

      return;
    }

    //  CLEAR ERROR
    setLocationError("");

    //  SET VALUES
    setFieldValue("location", street);
    setFieldValue("area", area);
    setFieldValue("state", state);
    setFieldValue("country", country);
    setFieldValue("lat", lat);
    setFieldValue("lng", lng);
  };
  useEffect(() => {
    arrangeData(true);
  }, [initialServiceData]);

  // Skip only the very first (mount) firing so a checkout-return's restored
  // crew selection survives it - selectedService can genuinely change again
  // later (vendor picks a different Service Category), and clearing crew
  // then is still correct, so this must not be gated by checkoutReturn for
  // the whole session, only for this one initial run.
  const skipInitialProviderClearRef = useRef(true);
  useEffect(() => {
    if (skipInitialProviderClearRef.current) {
      skipInitialProviderClearRef.current = false;
      return;
    }
    if (!group_id) {
      setProviderSelected([]);
    }
  }, [selectedService]);

  useEffect(() => {
    // Service step Required: the time is the first provider slot picked on the calendar.
    // (Optional: the time is chosen in the time list; a calendar pick sets it in handleAppointmentClick.)
    if (providerOptional) return;
    const first = providerSelected?.find((p) => p?.start && !p.restored);
    if (first) setTimeSelect(first.start.split("T")[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerSelected]);

  useEffect(() => {
    arrangeData();
    getPrice();
  }, [serviceData]);

  // The discount / quantity / tip controls change the price without touching serviceData.
  useEffect(() => {
    getPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountSpec, lineDiscounts, lineQty, tipPercent, activeCoupon, partner_id]);

  // Edit booking: editPartsSubtotal changing (parts added/removed via
  // PartsUsedSection) doesn't touch serviceData, so it needs its own trigger
  // to fold into the price summary/Balance Due.
  useEffect(() => {
    getPrice();
  }, [editPartsSubtotal]);
  // The chosen resource's pool, as the people who can do the service (professionals;
  // drivers are not in this grid). With no such person mapped nothing is filtered,
  // and a slot that is already selected always stays visible.
  const chosenResource = activeResources.find((r) => String(r.id) === String(resourceIds));
  const poolProfessionalIds = (chosenResource?.crew || [])
    .filter((c) => (c.skill_key || "PROFESSIONAL") === "PROFESSIONAL")
    .map((c) => Number(c.user_id));
  const poolFilterActive = poolProfessionalIds.length > 0 && !showAllCrew;

  const appointments = filteredAppointments
    ?.filter(
      (item) =>
        !poolFilterActive ||
        item?.status === 2 ||
        poolProfessionalIds.includes(Number(item?.extendedProps?.provider_id))
    )
    .map((item) => {
    const colors = getStatusColors(item?.status);

    return {
      title: `${item.title?.trim()}`,
      search: item.title?.trim(),
      resourceId: item.title?.trim(),
      start: item?.start,
      end: item?.end,
      backgroundColor: colors.bgColor,
      borderColor: colors.borderColor,
      textColor: colors.textColor,
      extendedProps: {
        ...item
      },
      // Optional: Add event class for additional styling
      className: `status-${item?.status || "default"}`
    };
  });

  const fetchDoctorHolidays = async (userId) => {
    try {
      const response = await apicall.get(
        `get_vendor_holidays?user_id=${userId}`
      );

      const holidays = response.data || [];

      const blocked = holidays.map((item) => {
        const d = new Date(item.date);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      });

      setVendorBlockedDates(blocked);
    } catch (error) {
      console.error("Error fetching holidays:", error);
      toast.error("Failed to fetch holidays");
    }
  };
  async function getAllServices() {
    const requestId = ++latestRequestRef.current;

    try {
      setLoading(true);

      const response = await apicall.get(
        `get_ezp_service?partner_id=${partner_id}`
      );

      // ❗ Ignore if not latest call
      if (requestId !== latestRequestRef.current) return;

      if (response.success && response.data) {
        setServices(response.data);

        if (group_id) {
          getDataByID(response.data);
        } else if (fromEstimationId && !customerSelectedRef.current) {
          loadFromEstimation(response.data, fromEstimationId);
        } else if (!customerSelectedRef.current) {
          setSelectedService(response.data?.[0]);
          fetchServiceData(response.data?.[0]);
          // A Profile change on the Service step lands here too - never wipe the customer the
          // user has already typed in (see userHasEditedForm).
          if (userHasEditedForm()) return;
          resetBookingForm();

          setInitialValues({
            partner: partner_id,
            org_id: "",
            userName: "",
            phoneNumber: "",
            email: "",
            house_no: "",
            area: "",
            location: "",
            location_id: "",
            state: "",
            landmark: "",
            country: vendorProfile?.currency === "INR" ? 2 : 1,
            lat: "",
            lng: "",
            status: 2, // In Progress — default service status for a new booking
            payment: ""
          });
        }
      }
    } catch (e) {
      console.log("e----", e);
      setServices([]);
    } finally {
      // Only stop loading for latest request
      if (requestId === latestRequestRef.current) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (initialValues.phoneNumber) {
      getRecentBookings(initialValues.phoneNumber);
    } else {
      setRecentBookings([]);
    }
  }, [initialValues.phoneNumber]);
  async function getDataByID(allServicesData) {
    try {
      setLoading(true);
      const response = await apicall.get(
        `get_ezp_booking_by_id?group_id=${group_id}`
      );
      console.log(response, "response---");
      setLoading(false);
      if (response.success && response.data && response.data.length > 0) {
        setExistingData(response?.data || []);
        let {
          customer_id,
          partner,
          org_id,
          user_name,
          first_name,
          middle_name,
          last_name,
          email,
          phone_number,
          house_no,
          area,
          state,
          location,
          location_id,
          landmark,
          status,
          payment,
          lat,
          lng,
          service_id,
          service_date,
          service_time,
          discount,
          display_booking_id,
          editable,
          additional_info,
          total_paid_amount,
          service_type_name,
          subscription_id
        } = response.data[0];
        // Backend concatenates first_name + last_name with SQL CONCAT, which
        // returns NULL entirely when last_name is null (e.g. customers synced
        // from another system with no last_name on record). Rebuild from the
        // individual name parts so a missing last_name doesn't blank out the name.
        const resolvedUserName =
          user_name ||
          `${first_name || ""} ${middle_name || ""} ${last_name || ""}`
            .replace(/\s+/g, " ")
            .trim();
        let d1 = {
          partner: partner,
          org_id: org_id,
          userName: cleanBackendValue(resolvedUserName),
          phoneNumber: cleanBackendValue(phone_number),
          email: cleanBackendValue(email),
          house_no: cleanBackendValue(house_no),
          area: cleanBackendValue(area),
          location: cleanBackendValue(location),
          location_id: location_id,
          state: cleanBackendValue(state),
          landmark: cleanBackendValue(landmark),
          country: vendorProfile?.currency === "INR" ? 2 : 1,
          lat: lat,
          lng: lng,
          status: status,
          payment: payment
        };
        setInitialValues(d1);

        setDateSelect(service_date?.split("T")?.[0] || service_date);
        setTimeSelect(hhmm(service_time));
        setDiscountSpec({ type: DISCOUNT_FLAT, value: Number(discount) || 0 });
        setDiscount(Number(discount) || 0);
        // Each offering's saved discount, as a flat amount on its line.
        setLineDiscounts(
          Object.fromEntries(
            response.data
              .filter((r) => Number(r.line_discount) > 0)
              .map((r) => [lineKey(r), { type: DISCOUNT_FLAT, value: Number(r.line_discount) }])
          )
        );
        setAdditionalNotes(additional_info);
        setPaidAmount(total_paid_amount);
        setExistingSubscriptionId(subscription_id || null);
        // Parse custom services from backend
        const customString = response.data[0]?.custom_items;
        // if (phone_number) {
        //   getRecentBookings(phone_number);
        // }
        if (customString) {
          const customArray = customString.split("||").map((item) => {
            const [name, amount] = item.split("::");

            return {
              id: Date.now() + Math.random(),
              name: name,
              amount: amount,
              isCustom: true
            };
          });

          setPriceSection(customArray);
        }
        setPartner_id(partner);
        setDisplay_booking_id(display_booking_id);
        setEditable(editable);
        setUser_id(customer_id);
        setActiveType(service_type_name || "null");

        // Crew: response.data has one row per service_offering in this booking
        // group, and each row's provider column holds ALL crew for that
        // offering as a comma-joined string (e.g. "197,237"). Split every row's
        // value and merge/dedupe across rows so crew show up correctly whether
        // there's one offering or several.
        let providers = [
          ...new Set(
            response.data
              .flatMap((r) => String(r.provider || "").split(","))
              .map((s) => Number(s.trim()))
              .filter((n) => Number.isFinite(n) && n > 0)
          )
        ];

        // Service Category must be matched against the service list for THIS
        // booking's real partner. allServicesData (passed in from getAllServices)
        // may still be the list fetched for the previous/default partner_id — from
        // before this booking's actual partner was known — which can be empty or
        // simply not include this service. Rather than depend on setPartner_id(...)
        // below indirectly retriggering a fresh getAllServices()/getDataByID() pass
        // (a fragile, easy-to-break cascade), fetch the authoritative list for the
        // confirmed-correct partner right here and match against that directly.
        let matchedService;
        try {
          const svcResponse = await apicall.get(
            `get_ezp_service?partner_id=${partner}`
          );
          if (svcResponse.success && svcResponse.data) {
            setServices(svcResponse.data);
            allServicesData = svcResponse.data;
          }
        } catch (e) {
          console.log("err fetching services for booking's partner----", e);
        }
        matchedService = allServicesData?.find((i) => i.id === service_id);

        let selectedId;
        if (matchedService) {
          selectedId = service_id;
          setSelectedService(matchedService);
        }
        let q1 = `getWokerAvailableSlots?date=${service_date}`;
        if (selectedId) {
          q1 += `&&service_id=${selectedId}`;
        }
        // setLoading(true);
        let getWorkerAvailable = await apicall.get(q1);

        let updatedData = getWorkerAvailable?.data || [];

        setInitialAppointments(getWorkerAvailable?.data || []);
        let dt = `${service_date}T${service_time}:00`;
        // console.log("dt---", dt);

        let p1 = [];
        for (let i of providers) {
          for (let j of updatedData) {
            if (i === j?.extendedProps?.provider_id && dt === j.start) {
              j.status = 2;
              p1.push(j);
            }
          }
        }
        // console.log("p1---", p1);
        // Everyone saved on the booking stays on it: those with no slot on this day's grid are kept as
        // restored entries (named from provider_people) instead of silently dropped.
        const onGrid = new Set(p1.map((j) => j?.extendedProps?.provider_id));
        p1.forEach((j) => {
          j.fromCalendar = false;
        });
        const people = parseProviderPeople(response.data);
        const restored = providers.filter((id) => !onGrid.has(id)).map((id) => restoredProviderEntry(id, people.get(id), dt));
        const selectedNow = [...p1, ...restored];
        setProviderSelected(selectedNow);
        setProvidersBaseline(providers.join(","));
        setProviderNotice("");

        // Save original snapshot for change detection
        partsChangedRef.current = false;
        // Service Specifics baseline is re-reported by the child once its
        // own fetch for this booking resolves.
        originalServiceSpecificsRef.current = null;
        // Re-baseline the parts-subtotal tracking too, in case this
        // component instance is being reused for a different booking rather
        // than freshly mounted (client-side route change between two edit
        // URLs) - otherwise the previous booking's baseline/subtotal would
        // leak into this one's balance-increase calculation.
        originalPartsSubtotalRef.current = null;
        setEditPartsSubtotal(0);
        // Establish the parts-subtotal baseline from a dedicated, independent
        // fetch rather than from PartsUsedSection's own onPartsChanged calls -
        // that component's first effect fires with subtotal=0 before its own
        // getBookingParts resolves, so "whichever call arrives first" is not
        // a safe way to know the booking's real starting subtotal (it either
        // wrongly baselines at 0 for a booking that already has parts, or a
        // later reset here can land after a real add and erase it). This
        // fetch runs in parallel with the rest of this function and with
        // PartsUsedSection's own fetch - a small duplication, but it removes
        // the race entirely rather than just narrowing it.
        inventoryService
          .getBookingParts(response.data[0].id)
          .then((res) => {
            const initialPartsSubtotal = (Array.isArray(res?.data) ? res.data : []).reduce(
              (sum, p) => sum + Number(p.quantity) * Number(p.unit_price),
              0
            );
            originalPartsSubtotalRef.current = initialPartsSubtotal;
            setEditPartsSubtotal(initialPartsSubtotal);
          })
          .catch(() => {
            originalPartsSubtotalRef.current = null;
          });
        originalSnapshotRef.current = {
          user_name: user_name || "",
          phone_number: String(phone_number || ""),
          email: email || "",
          house_no: house_no || "",
          area: area || "",
          location: location || "",
          state: state || "",
          landmark: landmark || "",
          lat: String(lat || ""),
          lng: String(lng || ""),
          service_date: service_date?.split("T")?.[0] || service_date || "",
          service_time: (service_time || "").slice(0, 5),
          additional_info: additional_info || "",
          discount: Number(discount || 0),
          total_paid_amount: Number(total_paid_amount || 0),
          status: Number(status || 0),
          payment: Number(payment || 0),
          offering_ids: response.data
            .map((d) => d.service_offering_id)
            .sort()
            .join(","),
          // Each service's saved price and the custom line items, so a price-only edit counts as a change.
          amounts: amountsSignature(response.data, "price"),
          line_discounts: amountsSignature(response.data, "line_discount"),
          custom_items: customItemsSignature(
            (response.data[0]?.custom_items || "")
              .split("||")
              .filter(Boolean)
              .map((item) => {
                const [name, amount] = item.split("::");
                return { name, amount, isCustom: true };
              })
          ),
          providers: providerIdsKey(selectedNow),
          resource_ids: String(response.data[0]?.resource_ids || "")
        };
        setResourceIds(String(response.data[0]?.resource_ids || ""));
        setResourceCurrentName(String(response.data[0]?.resource_names || ""));

        // if (!editable) {
        //   let ans = updatedData.filter((i) => {
        //     let hours = timeSelect.split(":")[0];
        //     let mins = timeSelect.split(":")[1];

        //     let res = i.start.split("T")?.[1];
        //     // console.log(
        //     //   "res--",
        //     //   res,
        //     //   `${hours}:${mins}:00`,
        //     //   res === `${hours}:${mins}:00`
        //     // );
        //     return res === `${hours}:${mins}:00`;
        //     //   if (res === `${hours}:${mins}:00`) {
        //     //   i.start = i.start.split("T")[0] + "T" + `${hours}:${mins}:00`;
        //     // }
        //   });
        //   updatedData = ans;
        // }

        // Use p1.length (local, already populated) instead of crewCount
        // (crewCount is a stale closure value = 0 at this point because
        //  setProviderSelected(p1) hasn't caused a re-render yet)
        const loadedCrewCount = selectedNow.length || 1;
        for (let i of updatedData) {
          if (i.status === 2) {
            let date = new Date(i?.start);
            let t1 = maxTime > 0 ? maxTime / loadedCrewCount : 60; // fallback 60 min
            date.setMinutes(date.getMinutes() + t1);
            i.end = convertWithoutTimseStamp(date);
          }
        }
        let u1 = cleanCrewSlots(updatedData);

        // No crew on the calendar that day (a vendor who books without a provider, or nobody has a
        // shift): there is nothing to measure - the default hours apply and the grid stays empty.
        if (u1.length === 0) {
          setTimeRange({ startTime: "08:00:00", endTime: "20:00:00" });
          setFilteredAppointments([]);
        } else {
          const result = u1.reduce(
            (acc, item) => {
              return {
                minStart: item.start < acc.minStart ? item.start : acc.minStart,
                maxEnd: item.end > acc.maxEnd ? item.end : acc.maxEnd
              };
            },
            {
              minStart: u1[0].start,
              maxEnd: u1[0].end
            }
          );
          let min1 = result?.minStart?.split?.("T")?.[1];
          let max1 = result?.maxEnd?.split?.("T")?.[1];
          if (!min1 || min1.includes("NaN")) min1 = "08:00:00";
          if (!max1 || max1.includes("NaN")) max1 = "20:00:00";
          setTimeRange({
            startTime: min1,
            endTime: max1
          });

          setFilteredAppointments(u1 || []);
        }
      } else {
      }
    } catch (e) {
      console.log("e----", e);
    } finally {
      setLoading(false);
      setEditLoaded(true);
    }
  }
  // Convert-to-booking pre-fill: reuses the exact same setters getDataByID
  // uses for the edit-booking load path (setInitialValues/setExistingData/
  // setSelectedService), just built from an estimation's fields instead of
  // a booking row. Deliberately does NOT call fetchServiceData directly -
  // setting selectedService/existingData here and letting the existing
  // `useEffect(() => fetchServiceData(selectedService), [selectedService])`
  // (below) fire is what getDataByID itself relies on, so this stays
  // consistent with that timing (existingData must land before
  // fetchServiceData's "mark added" matching runs against it).
  async function loadFromEstimation(allServicesData, estimationId) {
    try {
      setLoading(true);
      const response = await estimationService.getEstimation(estimationId);
      if (!response.success || !response.data) {
        toast.error(response.message || "Quotation not found");
        return;
      }
      const e = response.data;

      setInitialValues({
        partner: partner_id,
        org_id: "",
        userName: cleanBackendValue(e.customer_name),
        phoneNumber: cleanBackendValue(e.phone_number),
        email: cleanBackendValue(e.email),
        house_no: cleanBackendValue(e.house_no),
        area: cleanBackendValue(e.area),
        location: cleanBackendValue(e.location),
        location_id: "",
        state: cleanBackendValue(e.state),
        landmark: cleanBackendValue(e.landmark),
        country: e.country || (vendorProfile?.currency === "INR" ? 2 : 1),
        lat: e.lat || "",
        lng: e.lng || "",
        status: 2, // In Progress — default service status for a new booking
        payment: ""
      });
      customerSelectedRef.current = true;

      setDiscountSpec({ type: DISCOUNT_FLAT, value: Number(e.discount_value) || 0 });
      setDiscount(Number(e.discount_value) || 0);
      setAdditionalNotes(e.notes || "");

      // Synthesize the same shape getDataByID populates existingData with
      // (service_offering_id + price) so fetchServiceData's unmodified
      // "mark added" matching logic works against an estimation's
      // offerings exactly as it does against a booking's.
      const matchedService = allServicesData?.find(
        (s) =>
          String(s.id) === String(e.service_id) ||
          String(s.service_id) === String(e.service_id)
      );
      setExistingData(
        (e.offerings || []).map((o) => ({
          service_offering_id: o.service_offering_id,
          price: o.unit_price,
          service_id: matchedService?.service_id ?? e.service_id
        }))
      );
      if (matchedService) setSelectedService(matchedService);

      // Seed staged inventory parts (StagedPartsSection) from the
      // estimation's items - same row shape addStagedPart builds.
      const partRows = (e.items || []).map((i) => ({
        name: i.item_name,
        amount: Number(i.quantity) * Number(i.unit_price),
        isPart: true,
        item_id: i.item_id,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price),
        unit_of_measure: i.unit_of_measure,
        id: `part-${i.item_id}-${Date.now()}`
      }));
      setPriceSection(partRows);
      getPrice(partRows);
    } catch (err) {
      console.log("err loading estimation----", err);
      toast.error("Failed to load quotation");
    } finally {
      setLoading(false);
    }
  }
  function getPrice(basisPriceSection) {
    try {
      // Accepting an explicit basis (rather than always reading the `priceSection` closure)
      // matters when a caller just spliced a new row (addStagedPart / removeStagedPart) and
      // calls getPrice(copy) in the same tick: setPriceSection(copy) and this function's own
      // setPriceSection(...) batch together, so reading the pre-splice array would silently
      // discard the change.
      const basis = basisPriceSection || priceSection;
      const result = computePriceSection({
        serviceData,
        basis,
        editPartsSubtotal,
        taxPercentage,
        discountSpec,
        lineDiscounts,
        // Quantity and tip belong to a new booking's checkout; a saved booking keeps its own total.
        lineQty: isEditing ? {} : lineQty,
        tipPercent: isEditing ? 0 : tipPercent,
        coupon: activeCoupon,
        partnerId: partner_id
      });
      setPriceSection(result.rows);
      setDiscount(result.discountAmount);
    } catch (e) {
      console.log("e-----", e);
    }
  }

  async function arrangeData(d1) {
    // serviceData also carries offerings added under other Service
    // Categories (multi-category bookings) — the Sub Category / Service Type
    // grid only shows the selected category's own offerings.
    const selId = selectedService?.service_id;
    let g1 = serviceData.reduce((acc, item) => {
      if (selId != null && String(item?.service_id) !== String(selId)) {
        if (d1) item.added = false;
        return acc;
      }
      // console.log("acc--", acc);
      let { service_type_name } = item;
      // console.log(service_type_name);
      if (!acc[service_type_name]) acc[service_type_name] = [];

      if (d1) item.added = false;
      acc[service_type_name].push(item);
      return acc;
    }, {});

    setServiceTypes(Object.keys(g1));
    setGroupedData(g1);
  }
  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([
          fetchCountries(),
          getBookingStatus(),
          getPaymentStatus()
        ]);
      } catch (error) {
        console.error("Error initializing data:", error);
        toast.error("Failed to initialize form data");
      }
    };

    initializeData();
  }, []);
  const fetchServiceData = async (data, partnerID = null) => {
    const requestId = ++latestServiceFetchRef.current;
    try {
      // When coming back from Checkout via "+ Add Item", reuse saved service data
      if (checkoutReturnServiceDataRef.current) {
        const saved = checkoutReturnServiceDataRef.current;
        checkoutReturnServiceDataRef.current = null;
        let sumTime = 0;
        for (const i of saved) {
          if (i?.added) sumTime += (i?.time_required || 0) + (i?.buffer || 0);
        }
        setMaxTime(sumTime);
        setServiceData(saved);
        return;
      }
      if (data?.service_id) {
        // An explicitly-passed partnerID (e.g. the Profile dropdown's onChange,
        // which fires fetchServiceData in the same handler as setPartner_id)
        // must win over the partner_id state, which is still the OLD value
        // here — setPartner_id hasn't taken effect yet when this line runs, so
        // preferring state over the parameter fetched Sub Category/Service Type
        // for the partner being switched AWAY from, not the one being switched to.
        let p1 = partnerID != null ? partnerID : partner_id || 1;
        setLoading(true);
        const response = await apicall.get(
          `offeringFromService?service_id=${data?.service_id}&&is_partner=true&&partner_id=${p1}`
        );
        setLoading(false);

        // A newer call (e.g. the real booking data loading in) has since
        // started — this response is stale, don't let it clobber the correct
        // Service Category/Sub Category/Service Type selection.
        if (requestId !== latestServiceFetchRef.current) return;

        // console.log("existingData----->>>>", existingData);
        let copyResponse;
        if (response?.data) {
          copyResponse = JSON.parse(JSON.stringify(response?.data));
          // offeringFromService returns both active and inactive offerings
          // (Admin Setup needs to show inactive ones so they can be
          // reactivated) — while booking, only active ones should be
          // selectable here.
          copyResponse = copyResponse.filter(
            (item) => Number(item.is_active) === 1
          );
        }

        // A booking can hold offerings from several Service Categories, but
        // this fetch only returns the offerings of ONE category — replacing
        // serviceData with it wiped out whatever was added under the category
        // the vendor just switched away from. Carry the other categories'
        // offerings (and this category's own in-session add/remove/price
        // edits) forward from the current serviceData, unless the booking
        // being edited (existingData) or the Profile changed since the last
        // fetch — then start fresh from existingData like before.
        const keepPrev =
          lastServiceFetchKeyRef.current.existingData === existingData &&
          String(lastServiceFetchKeyRef.current.partner) === String(p1);
        lastServiceFetchKeyRef.current = { existingData, partner: p1 };
        const prevList = keepPrev ? serviceDataRef.current || [] : [];
        const currentServiceId = String(data.service_id);

        let offering_id = [];
        let type_name;

        if (response.success && copyResponse) {
          for (let i of existingData) {
            for (let j of copyResponse) {
              if (i.service_offering_id === j.service_offering_id) {
                // console.log("is there-----");
                j.added = true;
                if (!offering_id.includes(i.service_offering_id)) {
                  j.amount = Number(i.price);
                  offering_id.push(i.service_offering_id);
                }
                break;
              }
            }
          }
          // console.log("offering_id---", offering_id);
          // console.log("copyResponse---", copyResponse);

          let c1 = copyResponse?.map((ele) => {
            if (offering_id.includes(ele?.service_offering_id)) {
              if (!type_name) {
                type_name = ele?.service_type_name;
                setActiveType(ele?.service_type_name);
              }

              return { ...ele, added: true };
            } else {
              return { ...ele, added: false };
            }
          });

          let sumTime = 0;
          for (let i of copyResponse) {
            if (i?.added) {
              let t1 = i?.time_required + i?.buffer;
              sumTime += t1;
            }
          }
          setMaxTime(sumTime);

          const pendingSub = pendingSubRef.current;
          if (pendingSub) {
            pendingSubRef.current = null;
            const subOfferingId = pendingSub.service_offering_id;
            c1 = c1.map((item) => ({
              ...item,
              added: item.service_offering_id === subOfferingId
            }));
            setActiveType(pendingSub.service_type_name || "null");
          } else {
            const prevById = new Map(
              prevList.map((i) => [String(i.service_offering_id), i])
            );
            // Restore this category's selection from earlier in the session
            // (vendor added/removed/re-priced here, switched away, came back).
            if (keepPrev) {
              c1 = c1.map((item) => {
                const p = prevById.get(String(item.service_offering_id));
                return p ? { ...item, added: p.added, amount: p.amount } : item;
              });
            }

            let others;
            if (keepPrev) {
              others = prevList.filter(
                (i) => String(i.service_id) !== currentServiceId
              );
            } else {
              // Fresh load: existingData rows from the booking's OTHER
              // categories haven't been fetched yet — seed them from the
              // booking rows themselves so they stay on the booking (and in
              // the price summary) until the vendor opens that category.
              const seen = new Set();
              others = existingData
                .filter((r) => {
                  const k = String(r?.service_offering_id);
                  if (
                    !r?.service_offering_id ||
                    String(r.service_id) === currentServiceId ||
                    seen.has(k)
                  )
                    return false;
                  seen.add(k);
                  return true;
                })
                .map((r) => ({
                  service_id: r.service_id,
                  service_name: r.service_name,
                  service_type_id: r.service_type_id,
                  service_type_name: r.service_type_name,
                  service_offering_id: r.service_offering_id,
                  service_offering_name: r.service_offering_name,
                  time_required: r.time_required,
                  buffer: r.buffer,
                  price: Number(r.price),
                  amount: Number(r.price),
                  is_active: 1,
                  added: true
                }));
            }
            c1 = [...c1, ...others];

            const firstAdded = c1.find(
              (i) => i.added && String(i.service_id) === currentServiceId
            );
            const nextType = firstAdded
              ? firstAdded.service_type_name
              : copyResponse[0]?.service_type_name;
            setActiveType(nextType ? nextType : "null");
          }

          let finalSumTime = 0;
          for (let i of c1) {
            if (i?.added)
              finalSumTime += (i?.time_required || 0) + (i?.buffer || 0);
          }
          setMaxTime(finalSumTime);
          setServiceData(c1);
        } else {
          // Don't drop offerings already added under other categories just
          // because this one has none.
          setServiceData(
            prevList.filter((i) => String(i.service_id) !== currentServiceId)
          );
        }
      }
    } catch (error) {
      console.log("err---", error);
      toast.error("Failed to fetch service offerings....");
      setInitialServiceData([]);
      setServiceData([]);
    } finally {
      setLoading(false);
    }
  };
  const crewCount = providerSelected?.length || 0;

  // Optional resource field: shown only when the vendor has resources (or this
  // booking already holds one that has since been deactivated).
  const showResourceField = activeResources.length > 0 || !!resourceIds;
  const resourceLabel = isSportsService
    ? "Court / Field"
    : isCarService
    ? "Bay / Equipment"
    : isSalonService
    ? "Chair / Station"
    : "Resource";
  const resourceCrewIds = [
    ...new Set(
      (providerSelected || [])
        .map((p) => p?.extendedProps?.provider_id)
        .filter((id) => id !== undefined && id !== null && id !== "")
    )
  ];
  // Same wall-clock rule the server uses: the crew work in parallel, so the
  // resource is held for the total work divided across them.
  const resourceTime = (
    providerSelected?.[0]?.start?.split?.("T")?.[1] ||
    timeSelect ||
    ""
  ).slice(0, 5);
  const resourceDurationMin = Math.max(
    Math.round((maxTime || 0) / Math.max(resourceCrewIds.length, 1)),
    30
  );

  const serviceDuration = maxTime / 60;
  // groupedData?.[activeType]?.find((d) => d.added)?.time_required / 60 || 0;

  const durationPerCleaner = maxTime / crewCount;
  // crewCount > 0 ? Math.ceil((serviceDuration * 60) / crewCount) : 0;
  const isDataReady = maxTime > 0 && serviceData?.length > 0;

  const totalReservedHours = maxTime / 60;
  // crewCount > 0 ? (crewCount * serviceDuration).toFixed(2) : 0;
  const fetchPartners = async () => {
    try {
      const response = await apicall.get("partners");
      setPartners(response.data);

      if (!group_id && !customerSelectedRef.current) {
        setPartner_id(response?.data[0]?.id);
        if (userHasEditedForm()) {
          formikRef.current.setFieldValue("partner", response?.data[0]?.id);
          return;
        }
        // Functional update - payment deliberately left out here so it
        // doesn't stomp whatever getPaymentStatus's own default-selection
        // sets (that effect runs independently/in parallel with this one,
        // and either can resolve first).
        setInitialValues((prev) => ({
          ...prev,
          partner: response?.data[0]?.id,
          org_id: "",
          userName: "",
          phoneNumber: "",
          email: "",
          house_no: "",
          area: "",
          location: "",
          location_id: "",
          state: "",
          landmark: "",
          country: vendorProfile?.currency === "INR" ? 2 : 1,
          lat: "",
          lng: "",
          status: 2 // In Progress — default service status for a new booking
        }));
      }
    } catch (error) {
      console.error("Error fetching fetchPartners:", error);
      toast.error("Failed to fetch fetchPartners");
    }
  };

  // Show only this vendor's industry-specific statuses when
  // ezp_booking_status has any (e.g. Car Service's own Booked/Checked
  // In/.../Ready for Delivery set), and only fall back to the generic
  // (industry IS NULL) rows - never the whole table across every
  // industry - when none are configured. See utils/bookingStatus.js.
  const getBookingStatus = async () => {
    try {
      const profileRes = await apicall.get("ezpMyProfile");
      const vendorIndustry = profileRes?.data?.[0]?.industry;
      setBookingStatus(await fetchBookingStatusesForIndustry(apicall.get, vendorIndustry));
    } catch (error) {
      console.error("Error fetching Booking Status:", error);
      toast.error("Failed to fetch Booking Status:");
    }
  };

  const getRecentBookings = async (ph_number) => {
    try {
      if (!ph_number) {
        setRecentBookings([]);
        return;
      }
      const response = await apicall.get(
        `getRecentBookings/${ph_number}/${vendor_id}`
      );

      if (response.success && response.data) {
        setRecentBookings(response.data || []);
      } else {
        setRecentBookings([]);
      }
    } catch (error) {
      console.error("Error fetching Booking Status:", error);
      toast.error("Failed to fetch Booking Status:");
    }
  };

  const getPaymentStatus = async () => {
    try {
      const response = await apicall.get("get_ezp_payment_status");
      if (response.success) {
        setPaymentStatus(response.data);
        // New booking only (keyed off group_id, not touching edit mode at
        // all) - defaults to the first option in the list so the dropdown
        // isn't blank; the vendor can still change it before submitting.
        if (!group_id && response.data?.length) {
          if (userHasEditedForm()) {
            if (!formikRef.current.values.payment) formikRef.current.setFieldValue("payment", response.data[0].id);
          } else {
            setInitialValues((prev) => ({ ...prev, payment: response.data[0].id }));
          }
        }
        // Cash payment status of a new booking defaults to Pending.
        const pending = response.data?.find((p) => (p.name || "").toLowerCase() === "pending");
        if (response.data?.length) setCashPaymentStatus((pending || response.data[0]).id);
      } else {
        setPaymentStatus([]);
      }
    } catch (error) {
      console.error("Error fetching Booking Status:", error);
      toast.error("Failed to fetch Booking Status:");
    }
  };

  const fetchCountries = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await apicall.get("country", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.success) {
        const countriesData = Array.isArray(response.data)
          ? response.data
          : response.data?.countries || [];

        setCountries(countriesData);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast.error("Failed to fetch countries");
    }
  };

  const fetchStates = async (countryId1) => {
    try {
      // console.log("countryId----", countryId);
      let countryId = currency === "INR" ? 2 : 1;
      const token = localStorage.getItem("token");
      const response = await apicall.get(`state/${countryId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.success && response.data) {
        setStates(response.data);
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to fetch states");
      setStates([]);
    }
  };

  const handleCountryChange = (e, setFieldValue) => {
    const countryId = e.value;

    setFieldValue("country", countryId);
    if (countryId) {
      fetchStates(countryId);
      // Reset state and city when country changes
      // setStates([]);
      // setCities([]);
      // setFieldValue("state", "");
      // setFieldValue("area", "");
    } else {
      // setStates([]);
      // setCities([]);
    }
  };
  useEffect(() => {
    if (initialValues.country) {
      fetchStates(initialValues.country);
    }
  }, [initialValues.country]);
  const handleStateChange = (e, setFieldValue) => {
    const stateId = e.target.value;

    setFieldValue("state", stateId);
    if (stateId) {
      // fetchCities(stateId);
      // Reset city when state changes
      // setCities([]);
      // setFieldValue("city", "");
    } else {
      // setCities([]);
    }
  };

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const get_user_details = async (ph_number, setFieldValue, setValues) => {
    const requestId = ++latestUserLookupRef.current;
    const response = await apicall.get(
      `get_users_details/${ph_number}`
    );

    // console.log("first---response---", response);

    // A newer lookup (user kept typing / corrected the number after an
    // earlier pause) has since started. Without this guard, a slow, stale
    // response for an old/incomplete number can resolve after the correct
    // one and silently wipe out the just-filled customer name (its "not
    // found" branch calls setFieldValue("userName", "")), which then fails
    // Yup's "required" check on submit even though the name was visibly
    // filled in moments earlier.
    if (requestId !== latestUserLookupRef.current) return;

    if (response.status === 200 && response.data.length > 0) {
      const user = response.data[0];
      // (collapsed: a customer with no middle name used to come out as "Hana  Q")
      const fullName = `${user.first_name} ${user.middle_name || ""} ${user.last_name || ""}`
        .replace(/\s+/g, " ")
        .trim();
      // const age = calculateAge(user.dob);
      const gender =
        user.gender === null
          ? "Not Available"
          : user.gender === 1
            ? "Male"
            : "Female";

      setUser_id(user?.id || 0);
      customerSelectedRef.current = true;
      setSelectedSubscription(null);

      // Fetch active Open+Flexi subscriptions for this customer
      if (user.id) {
        apicall
          .get(`user-flexi-subscriptions/${user.id}`)
          .then((res) => {
            setUserFlexiSubs(Array.isArray(res.data) ? res.data : []);
          })
          .catch((err) => {
            console.warn(
              "Flexi subscription fetch failed:",
              err?.message || err
            );
            setUserFlexiSubs([]);
          });
      }

      // Batch every field into a single update so Formik only runs ONE
      // validation pass, against the complete, correct values. Firing 11
      // separate setFieldValue calls each queued its own async validation —
      // clicking Checkout before the LAST one resolved could still catch a
      // stale "Customer name is required" result from earlier in the
      // sequence, even though userName was already correctly set.
      setValues((prev) => ({
        ...prev,
        userName: fullName,
        email: cleanBackendValue(user?.email),
        house_no: cleanBackendValue(user?.house_no),
        area: cleanBackendValue(user?.area),
        landmark: cleanBackendValue(user?.landmark),
        state: cleanBackendValue(user?.state),
        location: cleanBackendValue(user?.location),
        lat: user?.lat || "",
        lng: user?.lng || "",
        gender: gender,
        location_id: user?.location_id || ""
      }));

      let d1 = {
        house_no: user?.house_no || "",
        area: user?.area || "",
        location: user?.location || "",
        state: user?.state || "",
        landmark: user?.landmark || "",
        lat: user?.lat || "",
        lng: user?.lng || ""
      };
      setFirstLocation(d1);

      // setSelectedUserId(user.id);

      // // Also update formData state for consistency
      // setFormData((prev) => ({
      //   ...prev,
      //   userName: fullName,
      //   age: age.toString(),
      //   gender: gender,
      // }));
    } else {
      // Not an existing customer. The name is only blanked when an earlier lookup filled it (the
      // number was edited away from that customer) - never a name the user typed themselves,
      // which a lookup that resolved late used to wipe.
      const nameCameFromLookup = customerSelectedRef.current;
      setValues((prev) => ({
        ...prev,
        userName: nameCameFromLookup ? "" : prev.userName,
        age: "",
        gender: ""
      }));
      setUserFlexiSubs([]);
      setSelectedSubscription(null);
      customerSelectedRef.current = false;
    }
  };

  const debouncedCheckUserExists = useCallback(
    debounce(async (field, value, setFieldValue, setValues) => {
      try {
        get_user_details(value, setFieldValue, setValues);
        getRecentBookings(value, setFieldValue);
      } catch (error) {
        console.error(`Error in debounced check for ${field}:`, error);
      }
    }, 500),
    []
  );

  // ════════════════════════════════════════════════════════════════════════════
  //  Stepper: which step is showing, what has been completed, payment choices
  // ════════════════════════════════════════════════════════════════════════════
  const rootRef = useRef(null);
  const latestSlotsRef = useRef(0);
  const formikRef = useRef(null);
  // Formik runs with enableReinitialize, so any setInitialValues() resets every field to
  // initialValues. Once the user has typed something, late async defaults (partners, payment
  // status, a Profile change reloading services) must go into the live values instead, or the
  // customer details are silently wiped and the final submit bounces back to step 1.
  const userHasEditedForm = () => !!formikRef.current?.dirty;
  const [editLoaded, setEditLoaded] = useState(false);
  // Optional mode: the providers the booking had when it was opened (an edit sends them back so the
  // server only changes the provider if THIS edit did), the "removed because you changed the time"
  // notice, whether the provider panel (the crew calendar) is open, and a nudge to reload its grid.
  const [providersBaseline, setProvidersBaseline] = useState(undefined);
  const [providerNotice, setProviderNotice] = useState("");
  const [providerPanelOpen, setProviderPanelOpen] = useState(false);
  const [gridReloadTick, setGridReloadTick] = useState(0);
  // An existing booking opens on its last step (Review & Update); a new one starts at Customer.
  const [stepIndex, setStepIndex] = useState(isEditing ? 3 : 0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [invalidSteps, setInvalidSteps] = useState([]);
  // Why the Schedule step cannot be left: shown beside the time field until it is fixed
  // (a toast alone is easy to miss, and the step looked as if Next did nothing).
  const [scheduleError, setScheduleError] = useState("");
  // New booking only: how the customer pays. "Pay Online" is not enabled for vendors yet
  // (it explains that instead of selecting), exactly as on the old Checkout page.
  const [selectedPayment, setSelectedPayment] = useState("cash");
  const [cashPaymentStatus, setCashPaymentStatus] = useState(null);
  const [showOnlineDisabledModal, setShowOnlineDisabledModal] = useState(false);

  const crewLabel = crewNoun({ isSportsService, isCarService, isSalonService });
  const steps = useMemo(
    () => [
      { key: "customer", label: isSportsService ? "Player" : "Customer", hint: "Who it's for" },
      { key: "service", label: isSportsService ? "Sports Info" : "Service", hint: "What's needed" },
      { key: "schedule", label: `${crewLabel} & Schedule`, hint: "Who and when" },
      { key: "review", label: isEditing ? "Review & Update" : "Review & Pay", hint: "Price and payment" }
    ],
    [isSportsService, crewLabel, isEditing]
  );
  const stepKey = steps[stepIndex]?.key;

  // The same page can switch between bookings (or to a new one) without remounting.
  useEffect(() => {
    setStepIndex(isEditing ? steps.length - 1 : 0);
  }, [group_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToStep = (i) => {
    setStepIndex(i);
    rootRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  };
  const jumpToKey = (key) => goToStep(Math.max(0, steps.findIndex((s) => s.key === key)));

  // After switching step the field may only just have become visible.
  const focusField = (field) => {
    setTimeout(() => {
      const el = document.querySelector(`[name="${field}"]`);
      if (el) {
        el.scrollIntoView?.({ behavior: "smooth", block: "center" });
        el.focus?.({ preventScroll: true });
      }
    }, 60);
  };

  // A failed submit names the first bad field and takes the user to the step that holds it.
  const onSubmitErrorField = (field) => {
    const key = stepOfField(field);
    setInvalidSteps((prev) => (prev.includes(key) ? prev : [...prev, key]));
    jumpToKey(key);
    focusField(field);
  };

  const timeRequiredMessage = "Choose a time for the booking";
  const crewRequiredMessage =
    isCarService || isSalonService ? "Select Time and Service Provider" : "Select Time and Crew";

  // Say it twice: the toast, and a message that stays beside the time field until the
  // time is chosen. Brings the field into view in case the step is taller than the screen.
  const flagScheduleError = (message) => {
    setScheduleError(message);
    toast.error(message);
    setTimeout(() => {
      const el = document.querySelector('[data-field="schedule-time"]');
      if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 60);
  };

  useEffect(() => {
    if (!scheduleError) return;
    const chosen = providerOptional ? !!hhmm(timeSelect) : (providerSelected?.length || 0) > 0;
    if (chosen) setScheduleError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleError, providerOptional, timeSelect, providerSelected]);

  // The checks that are not Formik fields (they live in this component's state).
  const checkServiceSelections = () => {
    if (!services || services.length === 0) {
      toast.error("Please add Service Type and Offerings from Admin Setup before booking.");
      return false;
    }
    if (!serviceTypes || serviceTypes.length === 0) {
      toast.error("Please configure Service Offerings before proceeding.");
      return false;
    }
    if (serviceData?.filter((ele) => ele?.added).length === 0) {
      toast.error("Select Service Offerings to proceed");
      return false;
    }
    return true;
  };

  // Leaving a step: its Formik fields must be valid and its selections made. The final submit
  // checks everything again, so skipping ahead can never get around a rule.
  const validateStep = async (key) => {
    const formik = formikRef.current;
    if (formik && STEP_FIELDS[key]?.length) {
      const errs = await formik.validateForm();
      const bad = STEP_FIELDS[key].filter((f) => errs[f]);
      if (bad.length) {
        formik.setTouched({ ...formik.touched, ...Object.fromEntries(bad.map((f) => [f, true])) }, false);
        const raw = errs[bad[0]];
        toast.error(typeof raw === "string" ? raw : `${FIELD_LABELS[bad[0]] || bad[0]} is invalid`);
        focusField(bad[0]);
        return false;
      }
    }
    if (key === "service") {
      if (!checkServiceSelections()) return false;
      if (!group_id && serviceSpecifics.new_asset_error) {
        toast.error(serviceSpecifics.new_asset_error);
        return false;
      }
    }
    if (key === "schedule") {
      if (providerOptional) {
        if (!hhmm(timeSelect)) {
          flagScheduleError(timeRequiredMessage);
          return false;
        }
      } else if (providerSelected?.length === 0) {
        flagScheduleError(crewRequiredMessage);
        return false;
      }
    }
    return true;
  };

  const markStep = (key, ok) => {
    setCompletedSteps((prev) => (ok ? (prev.includes(key) ? prev : [...prev, key]) : prev.filter((k) => k !== key)));
    setInvalidSteps((prev) => (ok ? prev.filter((k) => k !== key) : prev.includes(key) ? prev : [...prev, key]));
  };

  const goNext = async () => {
    const ok = await validateStep(stepKey);
    markStep(stepKey, ok);
    if (ok && stepIndex < steps.length - 1) goToStep(stepIndex + 1);
    return ok;
  };
  const goBack = () => stepIndex > 0 && goToStep(stepIndex - 1);

  // A saved booking can be opened at any step; a new one only up to the first step not yet done.
  const canSelectStep = (i) =>
    isEditing || i <= stepIndex || steps.slice(0, i).every((s) => completedSteps.includes(s.key));
  const completedForDisplay = isEditing ? steps.map((s) => s.key) : completedSteps;

  // The crew calendar is part of the Schedule step: load the day when it opens.
  useEffect(() => {
    // (Optional mode: the crew calendar loads when its panel is opened, not with the step.)
    if (stepKey === "schedule" && !providerOptional) getWorkerAvailableSlots(dateSelect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  // ════════════════════════════════════════════════════════════════════════════
  //  Money
  // ════════════════════════════════════════════════════════════════════════════
  const pricing = computePriceSection({
    serviceData,
    basis: priceSection,
    editPartsSubtotal,
    taxPercentage,
    discountSpec,
    lineDiscounts,
    lineQty: isEditing ? {} : lineQty,
    tipPercent: isEditing ? 0 : tipPercent,
    coupon: activeCoupon,
    partnerId: partner_id
  });

  const totalItem = priceSection?.find((item) => item.name === "Total");
  const totalAmount = Number(totalItem?.amount || 0);

  const balanceDue = totalAmount - Number(paidAmount || 0);

  // Edit booking only: how much this session's part changes have added to
  // the balance due, tax-adjusted to match how getPrice() actually taxes the
  // combined total. Baselined against whatever the booking's parts subtotal
  // already was when opened (originalPartsSubtotalRef), so pre-existing
  // parts don't trigger this - only parts added/removed during this visit.
  const pendingBalanceIncrease =
    originalPartsSubtotalRef.current === null
      ? 0
      : (editPartsSubtotal - originalPartsSubtotalRef.current) * (1 + taxPercentage / 100);

  const setLineDiscount = (key, spec) => {
    setLineDiscounts((prev) => ({ ...prev, [key]: spec }));
  };
  const setLineQuantity = (key, q) =>
    setLineQty((prev) => ({ ...prev, [key]: Math.max(1, Math.floor(Number(q) || 1)) }));

  // Editing a service line's price. (The whole-order discount is its own control now, so the
  // old "Discount" price row no longer needs to be editable here.)
  function priceChange(value, data) {
    try {
      let copyServiceData = JSON.parse(JSON.stringify(serviceData));

      for (let eachData of copyServiceData) {
        if (eachData.service_offering_id === data?.service_offering_id) {
          eachData.amount = value;
        }
      }

      setServiceData(copyServiceData);
    } catch (e) {
      console.log("err-----", e);
    }
  }

  // Removing a custom line: the explicit basis lets getPrice rebuild from the copy (not from the
  // pre-removal array) without losing staged parts or the other custom items.
  function deleteCustomItem(index) {
    const copy = [...priceSection];
    copy.splice(index, 1);
    setPriceSection(copy);
    getPrice(copy);
  }

  function addCustomService() {
    const copy = [...priceSection];

    const taxIndex = copy.findIndex((item) =>
      item.name?.toLowerCase().includes("tax")
    );

    const newRow = {
      name: "",
      amount: "",
      isCustom: true,
      id: Date.now()
    };

    if (taxIndex !== -1) {
      copy.splice(taxIndex, 0, newRow); //  insert BEFORE tax
    } else {
      copy.push(newRow);
    }

    setPriceSection(copy);
  }

  function customNameChange(value, index) {
    const copy = [...priceSection];
    copy[index].name = value;
    setPriceSection(copy);
  }
  function customPriceChange(value, index) {
    const copy = [...priceSection];
    copy[index].amount = value;
    setPriceSection(copy);

    getPrice(copy);
  }

  // Staged inventory parts for a brand-new booking (StagedPartsSection).
  // Live purely as rows in priceSection - tagged isPart, mirroring how
  // isCustom rows already work - so they fold into getPrice()'s total the
  // same way, and survive unchanged into Checkout.jsx via
  // values.priceSection. Nothing is persisted here; the real
  // addBookingPart(...) calls happen only after the booking is actually
  // created (Checkout.jsx's flushStagedParts).
  function addStagedPart(item, quantity, unitPrice) {
    const qty = Number(quantity);
    const price = Number(unitPrice);
    const existingIdx = priceSection.findIndex(
      (row) => row?.isPart && row.item_id === item.id
    );
    let copy;
    if (existingIdx !== -1) {
      // Staging the same item again merges into the existing line (combined
      // quantity, latest unit price) rather than adding a duplicate row.
      const mergedQuantity = Number(priceSection[existingIdx].quantity) + qty;
      const merged = {
        ...priceSection[existingIdx],
        quantity: mergedQuantity,
        unit_price: price,
        amount: mergedQuantity * price
      };
      copy = [...priceSection];
      copy[existingIdx] = merged;
    } else {
      const newRow = {
        name: item.name,
        amount: qty * price,
        isPart: true,
        item_id: item.id,
        quantity: qty,
        unit_price: price,
        unit_of_measure: item.unit_of_measure,
        id: `part-${item.id}-${Date.now()}`
      };
      copy = [...priceSection, newRow];
    }
    setPriceSection(copy);
    getPrice(copy);
  }

  function removeStagedPart(rowId) {
    const copy = priceSection.filter((row) => row.id !== rowId);
    setPriceSection(copy);
    getPrice(copy);
  }
  const addOffering = (data) => {
    try {
      // console.log("data-----", data);

      // if (t1 > maxTime) {
      //   setMaxTime(t1);
      // }
      let { service_offering_id } = data;
      let copyServiceData = JSON.parse(JSON.stringify(serviceData));
      for (let eachdata of copyServiceData) {
        if (eachdata?.service_offering_id === service_offering_id) {
          eachdata.added = !eachdata.added;
          break;
        }
      }

      let sumTime = 0;
      for (let i of copyServiceData) {
        if (i?.added) {
          let t1 = i?.time_required + i?.buffer;
          sumTime += t1;
        }
      }
      setMaxTime(sumTime);
      setServiceData(copyServiceData);
    } catch (e) {
      console.log("err----", e);
    }
  };

  // ════════════════════════════════════════════════════════════════════════════
  //  Submit
  // ════════════════════════════════════════════════════════════════════════════
  const buildCtx = () => ({
    user_id,
    editable,
    selectedSubscription,
    existingSubscriptionId,
    dateSelect,
    firstLocation,
    providerSelected,
    timeSelect,
    serviceData,
    priceSection,
    showResourceField,
    resourceIds,
    formWorkflow,
    workflowAssign,
    group_id,
    fromEstimationId,
    additionalNotes,
    paidAmount,
    currency,
    providerOptional,
    providersBaseline: isEditing ? providersBaseline : undefined
  });

  // A new booking is created here (cash, or online via the payment gateway); the booking
  // confirmation opens once it exists.
  const checkout = useBookingCheckout({
    partners,
    partnerId: partner_id,
    currency,
    onBooked: (bookingRes, bookingValues) => {
      setModelData({ ...bookingValues, booking_id: bookingRes.booking_id });
      setShowOrderConf(true);
    }
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let flag = true;

      if (!services || services.length === 0) {
        toast.error("Please add Service Type and Offerings from Admin Setup before booking.");
        jumpToKey("service");
        return;
      }

      if (!serviceTypes || serviceTypes.length === 0) {
        toast.error("Please configure Service Offerings before proceeding.");
        jumpToKey("service");
        return;
      }
      let addedService = serviceData?.filter((ele) => ele?.added);
      if (addedService?.length === 0) {
        toast.error("Select Service Offerings to proceed");
        jumpToKey("service");
        flag = false;
      }

      if (providerOptional ? !hhmm(timeSelect) : providerSelected?.length === 0) {
        flagScheduleError(providerOptional ? timeRequiredMessage : crewRequiredMessage);
        if (flag) jumpToKey("schedule");
        flag = false;
      }

      if (!flag) {
        return;
      }

      const problems = pricingProblems({ result: pricing, paidAmount });
      if (problems.length) {
        toast.error(problems[0]);
        jumpToKey("review");
        return;
      }

      const ctx = buildCtx();
      const bookingValues = buildSubmitValues({ values, ctx });

      if (group_id) {
        // Service Specifics diff against what was loaded for this booking.
        const specificsNow = normalizeSpecifics(serviceSpecifics);
        const specificsDirty = specificsChanged(originalServiceSpecificsRef.current, serviceSpecifics);

        // Detect if anything actually changed before submitting
        if (originalSnapshotRef.current) {
          const unchanged = isBookingUnchanged({
            values,
            snap: originalSnapshotRef.current,
            ctx: {
              ...ctx,
              discount,
              specificsChanged: specificsDirty,
              workflowInitialSignature: workflowInitialSigRef.current
            }
          });

          if (unchanged) {
            if (!partsChangedRef.current) {
              toast.info("No changes detected. Please modify booking details before updating.");
              setSubmitting(false);
              return;
            }
            // Only parts changed: fall through to the normal update submit below, since parts
            // count toward the booking's own total / Balance Due and that must be persisted -
            // priceSection already reflects it via editPartsSubtotal.
          }
        }

        // Edit mode: update the booking directly
        setLoading(true);
        // No user_id / vendor_id here: the server takes the vendor from the login
        // (a staff login's own id would save the edit under the wrong vendor).
        // Respect whatever the vendor picked in the Status / Payment dropdowns; only fall back
        // to the existing / default value if nothing was selected.
        bookingValues.status = values.status || existingData[0]?.status || 2;
        bookingValues.payment = values.payment ?? existingData[0]?.payment ?? 1;

        const currentPartnerId = Number(values.partner || partner_id);
        const currentPartner = partners?.find((p) => p.id === currentPartnerId);
        const editApiName =
          currentPartner?.partner_name?.trim().toLowerCase() === "utilstop"
            ? "create_partner_booking"
            : "create_ezp_booking";

        const bookingRes = await apicall.post(bookingValues, editApiName);
        setLoading(false);
        if (bookingRes?.message && !bookingRes?.booking_id) {
          toast.error(bookingRes.message);
        } else if (bookingRes?.booking_id) {
          // Service Specifics lives in a separate table - the booking id is already known
          // here. Only fires if it differs from what was loaded, and clearing it persists too.
          if (specificsDirty) {
            apicall
              .post({ booking_id: existingData[0].id, ...serviceSpecifics }, "ezp_booking_service_details")
              .then((r) => {
                if (r?.success) originalServiceSpecificsRef.current = specificsNow;
              })
              .catch((e) => console.log(e));
          }
          setModelData({
            booking_id: bookingRes.booking_id,
            serviceData: bookingValues.serviceData,
            state: bookingValues.state,
            date: bookingValues.date,
            time: bookingValues.time,
            lat: bookingValues.lat,
            lng: bookingValues.lng,
            house_no: bookingValues.house_no,
            area: bookingValues.area,
            location: bookingValues.location,
            landmark: bookingValues.landmark,
            userName: bookingValues.userName,
            phoneNumber: bookingValues.phoneNumber
          });
          if (bookingRes.resource_conflicts) {
            toast.warn(
              `Saved, but the ${resourceLabel.toLowerCase()} is also booked at this time: ${describeConflict(
                bookingRes.resource_conflicts
              )}`
            );
          }
          if (bookingRes.workflow_warning) toast.warn(bookingRes.workflow_warning);
          // An issued invoice is a snapshot: a changed service price / custom item does not reach it.
          const snapNow = originalSnapshotRef.current;
          if (
            invoiceInfo &&
            snapNow &&
            (amountsSignature((ctx.serviceData || []).filter((d) => d.added)) !== snapNow.amounts ||
              lineDiscountsSignature(ctx.priceSection) !== snapNow.line_discounts ||
              customItemsSignature(ctx.priceSection) !== snapNow.custom_items)
          ) {
            toast.warn(
              `Saved, but invoice ${invoiceInfo.number || ""} was already issued and still shows the old amounts. Cancel and re-issue the invoice to reflect the new amounts.`
            );
          }
          if (formWorkflow) {
            // What was just saved is the new starting point for "did anything change", and the
            // panel should show who is really on each step now.
            workflowInitialSigRef.current = assignmentsSignature(formWorkflow, workflowAssign);
            getBookingWorkflow(group_id)
              .then((res) => {
                if (res?.success && res.data) setRunWorkflow(res.data);
              })
              .catch(() => {});
          }
          setShowOrderConf(true);
        } else {
          toast.error("Failed to update booking. Please try again.");
        }
      } else {
        // New booking. A staged new-asset draft with missing fields blocks the save up front.
        if (serviceSpecifics.new_asset_error) {
          toast.error(serviceSpecifics.new_asset_error);
          jumpToKey("service");
          return;
        }
        // Service Specifics can only be saved once the booking exists - carried on the payload
        // for useBookingCheckout to write afterwards.
        bookingValues.customer_asset_id = serviceSpecifics.customer_asset_id;
        bookingValues.new_asset_draft = serviceSpecifics.new_asset_draft;
        bookingValues.customer_complaint = serviceSpecifics.customer_complaint;
        bookingValues.action_taken = serviceSpecifics.action_taken;

        await checkout.createBooking({
          bookingValues,
          paymentMethod: selectedPayment,
          paidAmount,
          cashPaymentStatus,
          total: totalAmount
        });
      }
    } catch (error) {
      console.log("API Error:", error);
      setLoading(false);
      toast.error(error?.message || "Failed to create booking");
    }
  };


  // ════════════════════════════════════════════════════════════════════════════
  //  Crew availability
  // ════════════════════════════════════════════════════════════════════════════
  const handleAppointmentClick = (appointment) => {
    try {
      const patch = resolveSlotClick({
        appointment,
        filteredAppointments,
        initialAppointments,
        maxTime,
        crewCount
      });
      if (patch.ignored) return;
      if (patch.dateSelect !== undefined) setDateSelect(patch.dateSelect);
      if (patch.message) setCalenderMessage(patch.message);
      if (patch.providerSelected !== undefined) {
        // Picks made on the calendar belong to the slot they were made for (see pickTime).
        patch.providerSelected.forEach((p) => {
          p.fromCalendar = true;
        });
        setProviderSelected(patch.providerSelected);
        // Optional mode: the pick sets the booking's time too, so list and calendar never disagree.
        const first = providerOptional ? patch.providerSelected.find((p) => p?.start) : null;
        if (first) {
          setTimeSelect(hhmm(first.start));
          timeTouchedRef.current = true;
        }
        setProviderNotice("");
      }
      if (patch.timeRange) setTimeRange(patch.timeRange);
      if (patch.filteredAppointments) setFilteredAppointments(patch.filteredAppointments);
    } catch (err) {
      console.error("handleAppointmentClick error:", err);
    }
  };

  // ── Optional mode: the time list, and the providers behind it ─────────────────
  // Changing the day: a provider's calendar slot belongs to one day, so in Required mode the choice
  // starts again. In Optional mode only a provider PICKED ON THE CALENDAR is tied to the day; one
  // assigned earlier stays on the booking.
  const pickDate = (date) => {
    if (!date) return;
    setProviderSelected((prev) => (providerOptional ? prev.filter((p) => !p.fromCalendar) : []));
    setDateSelect(date);
    if (!providerOptional || providerPanelOpen) getWorkerAvailableSlots(date);
  };

  // Changing only the time: a provider picked on the calendar was picked FOR the old time, so they
  // are removed (and the vendor told); a provider assigned earlier keeps the booking.
  const pickTime = (time) => {
    const next = hhmm(time);
    if (!next || next === hhmm(timeSelect)) return;
    const dropped = providerSelected.filter((p) => p.fromCalendar);
    timeTouchedRef.current = true;
    setTimeSelect(next);
    if (dropped.length) {
      setProviderSelected(providerSelected.filter((p) => !p.fromCalendar));
      const names = dropped.map((p) => p?.extendedProps?.search || p?.title).filter(Boolean).join(", ");
      setProviderNotice(`${names || "The provider"} was chosen for the old time and was removed. Pick them again for the new time if needed.`);
      setGridReloadTick((t) => t + 1);
    } else {
      setProviderNotice("");
    }
  };

  // Taking a provider off the booking: also frees their selected slots on the calendar grid.
  const removeProvider = (providerId) => {
    const free = (list) =>
      list.map((slot) => {
        if (slot?.extendedProps?.provider_id !== providerId || slot.status !== 2) return slot;
        const end = new Date(slot.start);
        end.setMinutes(end.getMinutes() + 30);
        return { ...slot, status: 1, end };
      });
    setProviderSelected((prev) => prev.filter((p) => p?.extendedProps?.provider_id !== providerId));
    setFilteredAppointments((prev) => free(prev));
    setInitialAppointments((prev) => free(prev));
    setProviderNotice("");
    setGridReloadTick((t) => t + 1);
  };

  // The crew calendar (inside the provider panel) loads when the panel opens, and again after a
  // change that left slots hidden on the grid.
  useEffect(() => {
    if (providerOptional && providerPanelOpen && stepKey === "schedule") getWorkerAvailableSlots(dateSelect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerPanelOpen, gridReloadTick]);

  // Optional mode: the day's time slots, and what to tell the vendor about the chosen one.
  const vendorSlots = useVendorTimeSlots({
    enabled: providerOptional && (stepKey === "schedule" || stepKey === "review"),
    date: dateSelect,
    serviceId: selectedService?.service_id,
    duration: maxTime,
    excludeGroupId: group_id
  });
  const selectedSlot = providerOptional ? (vendorSlots.data?.slots || []).find((sl) => sl.time === hhmm(timeSelect)) || null : null;

  // Service step Optional: pre-select the nearest slot instead of an empty time. Only for a new booking,
  // only from a list that is for the day on screen (the hook keeps the previous day's list while the new
  // one loads), and never over a time the vendor chose. A pick of ours is replaced when the day or the
  // job length changes the list, and dropped if nothing in the new list is offered.
  useEffect(() => {
    if (!providerOptional || isEditing || timeTouchedRef.current) return;
    const list = vendorSlots.data;
    if (vendorSlots.loading || !list || list.date !== ymdOf(dateSelect)) return;
    const current = hhmm(timeSelect);
    if (current && current !== autoTimeRef.current) return;
    const next = pickNearestSlot(list);
    if (next === current) return;
    autoTimeRef.current = next;
    setTimeSelect(next);
  }, [providerOptional, isEditing, vendorSlots.data, vendorSlots.loading, dateSelect, timeSelect]);
  // A fully booked time is allowed - it just has to be impossible to miss.
  const slotOverrideNote = selectedSlot?.status === "full" ? slotMessage(selectedSlot) : "";

  const datesSet = (dateInfo) => {
    try {
      const d1 = dateInfo.startStr?.split("T")?.[0];
      // dateSelect is a string once a slot was clicked but a Date straight after the date picker.
      const current = dateSelect instanceof Date ? dateToYmd(dateSelect) : dateSelect;
      if (d1 !== current) {
        getWorkerAvailableSlots(d1);
      }
    } catch (e) {
      console.log("err---", e);
    }
  };

  const getWorkerAvailableSlots = async (selecDate) => {
    const requestId = ++latestSlotsRef.current;
    try {
      if (!selecDate) return;

      let d1 = new Date(selecDate);
      d1 = d1.toLocaleDateString("en-GB").split(/[/\-.]/);
      d1 = `${d1[2]}-${d1[1]}-${d1[0]}`;

      setLoading(true);

      let q1 = `getWokerAvailableSlots?date=${d1}`;
      if (selectedService?.service_id) {
        q1 += `&&service_id=${selectedService.service_id}`;
      }
      const getWorkerAvailable = await apicall.get(q1);

      // Crew holidays for the chosen service.
      let availabilityMap = {};
      if (selectedService?.service_id) {
        try {
          const holidayRes = await apicall.get(`get_service_crews_holidays?service_id=${selectedService.service_id}`);
          availabilityMap = holidayRes?.data?.crewWiseAvailability || {};
        } catch (err) {
          console.log("Holiday API failed", err);
          availabilityMap = {};
        }
      }

      // A newer date was picked while this one was loading: drop this answer.
      if (requestId !== latestSlotsRef.current) return;

      const result = buildDaySlots({
        raw: getWorkerAvailable?.data,
        availabilityMap,
        day: d1,
        providerSelected,
        maxTime,
        crewCount
      });

      setInitialAppointments(result.initialAppointments);
      setTimeRange(result.timeRange);
      setFilteredAppointments(result.slots);
      setOpen(false);
      setLoading(false);
    } catch (e) {
      console.log("err--", e);
      setLoading(false);
    }
  };

  const onHideOrderConf = () => {
    setShowOrderConf(false);
    if (group_id) navigate("/dashboard");
    else navigate("/payments");
  };

  function close() {
    setOpen(false);
  }

  // Leaving with unsaved input asks first (a saved booking just goes back).
  const requestCancel = (dirty) => {
    if (group_id) {
      navigate(-1);
      return;
    }
    const hasInput =
      dirty ||
      providerSelected.length > 0 ||
      (providerOptional && !!hhmm(timeSelect)) ||
      additionalNotes !== "" ||
      serviceData.some((s) => s.added);
    if (hasInput) setShowCancelConfirm(true);
    else navigate(-1);
  };

  const refreshInvoiceInfo = async () => {
    try {
      const res = await apicall.get(`get_ezp_booking_by_id?group_id=${group_id}`);
      setInvoiceInfo(toInvoiceInfo(res?.data?.[0]));
    } catch (e) {
      console.error("Could not refresh invoice info:", e);
    }
  };

  // Admin / vendor only (the API enforces the same roles).
  const canCancelInvoice = getPersonaForRole(apicall.getRole()) === PERSONA.ADMIN_VENDOR;

  const loadInvoiceImpacts = async () => {
    const res = await invoiceService.getInvoiceImpact({ invoiceId: invoiceInfo?.ref });
    const { invoice, bookings, has_payments } = res.data;
    return [
      {
        id: invoice.id,
        number: invoice.invoice_number,
        currency: invoice.currency || currency,
        hasPayments: has_payments,
        items: bookings.map((b) => ({
          key: String(b.group_id ?? `b${b.booking_id}`),
          label: b.display_booking_id || `Booking #${b.booking_id}`,
          detail: [b.customer_name, b.service_date].filter(Boolean).join(" - "),
          amount: b.total,
          paid: b.paid_amount,
        })),
      },
    ];
  };

  const cancelBookingInvoice = async (reason, impacts) => {
    const res = await invoiceService.cancelInvoice(impacts[0].id, reason);
    if (res?.status !== 200) throw new Error(res?.message || "Could not cancel the invoice");
    toast.success(`Invoice ${impacts[0].number} cancelled`);
    setShowCancelInvoice(false);
    await refreshInvoiceInfo();
  };

  // invoiceDate: 'YYYY-MM-DD' from the Generate Invoice popup.
  const generateInvoiceForBooking = async (invoiceDate) => {
    const booking = existingData?.[0];
    if (!booking) return;
    try {
      setLoading(true);
      const [vendorRes, partsRes, assetsByBookingId] = await Promise.all([
        apicall.get("get_ezp_vendor_data"),
        inventoryService.getBookingParts(booking.id),
        getInvoiceAssets([booking.id]),
      ]);
      const parts = Array.isArray(partsRes?.data) ? partsRes.data : [];

      const addedItems = (booking.custom_items || "")
        .split("||")
        .map((pair) => {
          const [name, price] = pair.split("::");
          return name ? { name: name.trim(), price } : null;
        })
        .filter(Boolean);

      // One invoice line per offering: existingData has a row per offering
      // (name + price); booking-level total/tax/discount/paid come from the
      // first row and are split across those lines by splitIntoOfferings.
      const { htmlContent, booking_ids } = buildInvoiceHtml({
        bookings: splitIntoOfferings(
          {
            booking_id: booking.id,
            group_id: group_id,
            display_booking_id: booking.display_booking_id,
            org_id: booking.org_id,
            service_name: booking.service_name,
            service_type_name: booking.service_type_name,
            service_offering_name: booking.service_offering_name,
            service_date: booking.service_date,
            service_time: booking.service_time,
            total: booking.total,
            tax: booking.tax,
            discount: booking.discount,
            total_paid_amount: booking.total_paid_amount,
            payment: booking.payment,
            commission: booking.commission,
            partner_name: booking.partner_name,
            user_name: booking.user_name,
            house_no: booking.house_no,
            area: booking.area,
            landmark: booking.landmark,
            location: booking.location,
            state: booking.state,
            phone_number: booking.phone_number,
            addedItems,
          },
          existingData
        ),
        partsByBookingId: { [booking.id]: parts },
        assetsByBookingId,
        vendorData: vendorRes?.data,
        currency,
        isClinicService: false,
        invoiceDate,
      });

      await apicall.generate_pdf({ booking_ids, htmlContent, currency, invoice_date: invoiceDate }, "generate_pdf");
      toast.success("Invoice generated");
      await refreshInvoiceInfo();
    } catch (err) {
      console.error("Generate invoice failed:", err);
      toast.error(invoiceService.invoiceErrorMessage(err, "Failed to generate invoice"));
    } finally {
      setLoading(false);
    }
  };
  // Everything the steps read, in one object (they live at module level so typing in one
  // never remounts another - a component defined inside this one would).
  const b = {
    activeResources,
    activeType,
    addCustomService,
    addOffering,
    addStagedPart,
    additionalNotes,
    appointments,
    balanceDue,
    bookingStatus,
    calenderMessage,
    cashPaymentStatus,
    checkoutSubmitting: checkout.submitting,
    chosenResource,
    countries,
    crewCount,
    crewLabel,
    currency,
    customNameChange,
    customPriceChange,
    dateSelect,
    datesSet,
    debouncedCheckUserExists,
    deleteCustomItem,
    discountSpec,
    couponOptions,
    activeCoupon,
    setCouponId,
    display_booking_id,
    durationPerCleaner,
    editable,
    existingData,
    fetchServiceData,
    formWorkflow,
    getWorkerAvailableSlots,
    goBack,
    goNext,
    scheduleError,
    group_id,
    groupedData,
    handleAppointmentClick,
    handleCountryChange,
    handleServiceSpecificsLoaded,
    handleStateChange,
    industry,
    initialAppointments,
    isCarService,
    isDataReady,
    isEditing,
    isLoaded,
    isSalonService,
    isSportsService,
    lineDiscounts,
    lineQty,
    locationError,
    modelData,
    navigate,
    onHideOrderConf,
    onLoad,
    onPlaceChanged,
    onSubmitErrorField,
    open,
    originalPartsSubtotalRef,
    originalSnapshotRef,
    paidAmount,
    partners,
    partsChangedRef,
    paymentStatus,
    pendingBalanceIncrease,
    pendingSubRef,
    poolFilterActive,
    poolProfessionalIds,
    priceChange,
    priceSection,
    pricing,
    providerSelected,
    recentBookings,
    removeStagedPart,
    requestCancel,
    resourceCurrentName,
    resourceDurationMin,
    resourceIds,
    resourceLabel,
    resourceTime,
    runWorkflow,
    selectedPayment,
    selectedService,
    selectedSubscription,
    serviceData,
    serviceDuration,
    serviceTypes,
    services,
    setActiveType,
    setAdditionalNotes,
    setCalenderMessage,
    setCashPaymentStatus,
    setDateSelect,
    setDiscountSpec,
    setEditPartsSubtotal,
    setLineDiscount,
    setLineQuantity,
    setOpen,
    setPaidAmount,
    setPartner_id,
    setProviderSelected,
    setResourceIds,
    setRunWorkflow,
    setSelectedPayment,
    setSelectedService,
    setSelectedSubscription,
    setServiceSpecifics,
    setShowAllCrew,
    setShowCancelConfirm,
    setShowOnlineDisabledModal,
    setTipPercent,
    setWorkflowAssign,
    showCancelConfirm,
    showOnlineDisabledModal,
    showOrderConf,
    showResourceField,
    showRunWorkflow,
    states,
    stepIndex,
    stepKey,
    steps,
    taxPercentage,
    timeRange,
    timeSelect,
    tipPercent,
    totalAmount,
    totalReservedHours,
    userFlexiSubs,
    user_id,
    vendorBlockedDates,
    workflowAssign,
    workflowInitial,
    providerOptional,
    providerNotice,
    providerPanelOpen,
    setProviderPanelOpen,
    vendorSlots,
    selectedSlot,
    slotOverrideNote,
    pickDate,
    pickTime,
    removeProvider
  };

  return (
    <>
      <div ref={rootRef} className="bstep" onClick={close}>
        <div className="bstep-header">
          <h4>{group_id ? `Edit ${getBookingLabel(industry)} - ${display_booking_id}` : `New ${getBookingLabel(industry)}`}</h4>
          <div className="bstep-header-actions">
            {group_id && Number(existingData?.[0]?.status) === 4 && !invoiceInfo && (
              <Button type="button" variant="outline-danger" size="sm" className="btn btn-danger" disabled={loading} onClick={() => setShowInvoiceDate(true)}>
                Generate Invoice
              </Button>
            )}
            {group_id && invoiceInfo && (
              <>
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  disabled={!invoiceInfo.url}
                  onClick={() => window.open(invoiceInfo.url, "_blank", "noopener")}
                >
                  View Invoice {invoiceInfo.number}
                </Button>
                {canCancelInvoice && (
                  <Button type="button" variant="outline-danger" size="sm" disabled={loading} onClick={() => setShowCancelInvoice(true)}>
                    Cancel Invoice
                  </Button>
                )}
              </>
            )}
            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              className="btn btn-danger"
              onClick={() => {
                window.open(`/book-service/vendor/${vendor_id}`, "_blank");
              }}
            >
              Booking Link
            </Button>
          </div>
        </div>

        <InvoiceCancelDialog
          show={showCancelInvoice}
          onHide={() => setShowCancelInvoice(false)}
          loadImpacts={loadInvoiceImpacts}
          selectedKeys={new Set([String(group_id)])}
          noun="booking"
          onConfirm={cancelBookingInvoice}
        />

        <InvoiceDateDialog
          show={showInvoiceDate}
          onHide={() => setShowInvoiceDate(false)}
          count={1}
          onConfirm={(invoiceDate) => generateInvoiceForBooking(invoiceDate)}
        />

        <div className="bstep-stepper">
          <Stepper
            steps={steps}
            current={stepIndex}
            completed={completedForDisplay}
            invalid={invalidSteps}
            canSelect={canSelectStep}
            onSelect={goToStep}
          />
        </div>

        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {(formik) => <BookingStepperForm b={b} formik={formik} />}
        </Formik>

        <BookingModals b={b} />

        {loading && <div className="bstep-progress" role="progressbar" aria-label="Loading" />}
        {isEditing && !editLoaded && (
          <div className="bstep-overlay">
            <Spinner animation="border" variant="danger" />
          </div>
        )}
      </div>
    </>
  );
}

export default BookingStepper;
